import React, { useState, useEffect } from 'react';
import { X, BarChart3, Users, Share2, CheckCircle } from 'lucide-react';
import { pollsAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface Question {
  id: number;
  question_text: string;
  question_type: 'text' | 'textarea' | 'radio' | 'checkbox' | 'dropdown' | 'rating' | 'date';
  is_required: boolean;
  options: string[] | null;
  display_order?: number;
}

interface Response {
  id: number;
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  response_data: Record<number, any>;
  submitted_at: string;
}

interface Poll {
  id: number;
  title: string;
  description: string | null;
  is_anonymous: boolean;
  allow_share_results: boolean;
  shared_with_user_ids: string | null;
  responses: Response[];
  questions: Question[];
  response_count: number;
  created_by_id: number;
}

interface PollResultsViewProps {
  isOpen: boolean;
  onClose: () => void;
  poll: Poll | null;
  currentUserId: number;
  onUpdate?: () => void;
}

const PollResultsView: React.FC<PollResultsViewProps> = ({ isOpen, onClose, poll, currentUserId, onUpdate }) => {
  const [pollData, setPollData] = useState<Poll | null>(poll);
  const [isLoading, setIsLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (poll && isOpen) {
      setPollData(poll);
      fetchPollDetails();
      if (poll.allow_share_results) {
        fetchUsers();
      }
    }
  }, [poll, isOpen]);

  const fetchPollDetails = async () => {
    if (!poll) return;
    setIsLoading(true);
    try {
      const updatedPoll = await pollsAPI.getPoll(poll.id);
      setPollData(updatedPoll);
    } catch (error) {
      console.error('Error fetching poll details:', error);
      toast.error('Failed to load poll details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const users = await usersAPI.getAllUsers();
      setAllUsers(users);
      // Pre-select users who already have access
      if (poll?.shared_with_user_ids) {
        const sharedIds = poll.shared_with_user_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        setSelectedUserIds(sharedIds);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleShareResults = async () => {
    if (!poll) return;
    setIsSharing(true);
    try {
      await pollsAPI.updatePoll(poll.id, {
        shared_with_user_ids: selectedUserIds
      });
      toast.success('Results sharing updated successfully');
      setShareModalOpen(false);
      if (onUpdate) {
        onUpdate();
      }
      fetchPollDetails();
    } catch (error: any) {
      console.error('Error sharing results:', error);
      toast.error(error.response?.data?.error || 'Failed to update sharing settings');
    } finally {
      setIsSharing(false);
    }
  };

  const calculateStatistics = (questionId: number, questionType: string, options: string[] | null) => {
    if (!pollData || !pollData.responses) return null;

    const questionResponses = pollData.responses.map(r => r.response_data[questionId]).filter(v => v !== undefined && v !== null && v !== '');

    if (questionType === 'rating') {
      const ratings = questionResponses.map(r => parseInt(r)).filter(r => !isNaN(r));
      if (ratings.length === 0) return null;
      const sum = ratings.reduce((a, b) => a + b, 0);
      const avg = sum / ratings.length;
      const min = Math.min(...ratings);
      const max = Math.max(...ratings);
      return { average: avg.toFixed(2), min, max, count: ratings.length };
    }

    if (['radio', 'dropdown'].includes(questionType) && options) {
      const counts: Record<string, number> = {};
      options.forEach(opt => counts[opt] = 0);
      questionResponses.forEach(resp => {
        if (counts[resp] !== undefined) {
          counts[resp]++;
        }
      });
      return counts;
    }

    if (questionType === 'checkbox' && options) {
      const counts: Record<string, number> = {};
      options.forEach(opt => counts[opt] = 0);
      questionResponses.forEach(resp => {
        if (Array.isArray(resp)) {
          resp.forEach(opt => {
            if (counts[opt] !== undefined) {
              counts[opt]++;
            }
          });
        }
      });
      return counts;
    }

    return null;
  };

  const canViewResults = () => {
    if (!pollData) return false;
    if (pollData.created_by_id === currentUserId) return true;
    if (pollData.allow_share_results && pollData.shared_with_user_ids) {
      const sharedIds = pollData.shared_with_user_ids.split(',').map(id => parseInt(id.trim()));
      return sharedIds.includes(currentUserId);
    }
    return false;
  };

  if (!isOpen || !pollData) return null;

  if (!canViewResults()) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <p className="text-gray-600">
            You don't have permission to view the results of this poll.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{pollData.title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {pollData.response_count || 0} response{pollData.response_count !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {pollData.created_by_id === currentUserId && pollData.allow_share_results && (
              <button
                onClick={() => setShareModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span>Share Results with Users</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {pollData.questions
            .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999))
            .map((question, index) => {
            const stats = calculateStatistics(question.id, question.question_type, question.options);
            
            return (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">
                  {index + 1}. {question.question_text}
                </h3>

                {/* Statistics */}
                {stats && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    {question.question_type === 'rating' && (
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Average:</span>
                          <span className="font-semibold ml-2">{stats.average}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Min:</span>
                          <span className="font-semibold ml-2">{stats.min}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Max:</span>
                          <span className="font-semibold ml-2">{stats.max}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Count:</span>
                          <span className="font-semibold ml-2">{stats.count}</span>
                        </div>
                      </div>
                    )}

                    {['radio', 'dropdown', 'checkbox'].includes(question.question_type) && (
                      <div className="space-y-2">
                        {Object.entries(stats).map(([option, count]: [string, any]) => {
                          const total = pollData.responses.length;
                          const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                          return (
                            <div key={option} className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm text-gray-700">{option}</span>
                                  <span className="text-sm font-medium text-gray-900">{count} ({percentage}%)</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-[var(--accent)] h-2 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Individual Responses */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Responses:</h4>
                  {pollData.responses.map((response, respIndex) => {
                    const answer = response.response_data[question.id];
                    if (answer === undefined || answer === null || answer === '') return null;

                    return (
                      <div key={respIndex} className="bg-white border border-gray-200 rounded p-3 text-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {Array.isArray(answer) ? (
                              <div>
                                <span className="text-gray-600">Selected: </span>
                                <span className="text-gray-900">{answer.join(', ')}</span>
                              </div>
                            ) : (
                              <span className="text-gray-900">{String(answer)}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 ml-4">
                            {!pollData.is_anonymous && response.first_name && response.last_name ? (
                              <span>{response.first_name} {response.last_name}</span>
                            ) : (
                              <span>Anonymous</span>
                            )}
                            <span className="ml-2">
                              {new Date(response.submitted_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Share Results Modal */}
        {shareModalOpen && (
          <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Share Results</h3>
                <button
                  onClick={() => setShareModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                {allUsers.map(user => (
                  <label key={user.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUserIds([...selectedUserIds, user.id]);
                        } else {
                          setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">
                      {user.first_name} {user.last_name} ({user.email})
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShareModalOpen(false)}
                  className="px-4 py-2 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareResults}
                  disabled={isSharing}
                  className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white hover:opacity-90 disabled:opacity-50"
                >
                  {isSharing ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PollResultsView;
